import { EditorLabelsProps } from '@lib/common';
import { AssignmentCreationLabelType } from '@module/form-nextjs-app';
import { AssignmentModel } from '@module/assignment';
import { Form, FormSummary } from '@module/form';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { RootReducerState } from '../../../redux/root.reducer';
import { SpacePageState } from 'redux/space';
import { buildAssignmentDetailUrl, buildFormEditUrl } from '../../../utils';
import { readSpaceFolderTrailFromQuery } from '../../../utils/space-url.util';

export const useFormList = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { t: assignmentTranslation } = useTranslation('assignment');

  const { spaceObjects }: SpacePageState = useSelector(
    (state: RootReducerState) => state.spaceObjects,
    shallowEqual,
  );

  const [isOpenFormRenameModal, setIsOpenFormRenameModal] = useState(false);
  const [isOpenFormDeleteModal, setIsOpenFormDeleteModal] = useState(false);
  const [isOpenFormMoveModal, setIsOpenFormMoveModal] = useState(false);
  const [formIdSelected, setFormIdSelected] = useState<string | null>(null);
  const [formNameSelected, setFormNameSelected] = useState<string | null>(null);
  const [isModalVisible, setModalVisibility] = useState(false);

  const forms = spaceObjects?.forms ?? [];

  const assignmentCreationLabels: AssignmentCreationLabelType = assignmentTranslation(
    'assignmentCreation',
    { returnObjects: true },
  );

  const onCloseFormRenameModal = useCallback(() => setIsOpenFormRenameModal(false), []);
  const onCloseFormDeleteModal = useCallback(() => setIsOpenFormDeleteModal(false), []);
  const onCloseFormMoveModal = useCallback(() => setIsOpenFormMoveModal(false), []);

  const onSelectForm = useCallback(
    (form: Form | FormSummary): void => {
      const trail = readSpaceFolderTrailFromQuery(router.query);
      const url = buildFormEditUrl(form.id.toString(), form.title, {
        folderId: trail?.folderId ?? (router.query.folder as string),
        folderIds: trail?.folderIds,
        folderNames: trail?.folderNames,
      });
      router.push(url);
    },
    [router],
  );

  const onCreateAssignmentSuccess = useCallback(
    (assignmentInfo: AssignmentModel) => {
      const redirectUrl = buildAssignmentDetailUrl(assignmentInfo);
      router.push(redirectUrl);
    },
    [router],
  );

  const editorLabels: EditorLabelsProps = useMemo(
    () => ({
      addColumnBefore: t('addColumnBefore'),
      addColumnAfter: t('addColumnAfter'),
      deleteColumn: t('deleteColumn'),
      addRowBefore: t('addRowBefore'),
      addRowAfter: t('addRowAfter'),
      deleteRow: t('deleteRow'),
      deleteTable: t('deleteTable'),
      mergeCells: t('mergeCells'),
      splitCell: t('splitCell'),
      basicColors: t('basicColors'),
      bold: t('bold'),
      italic: t('italic'),
      moreFormatting: t('moreFormatting'),
      underline: t('underline'),
      strike: t('strike'),
      alignLeft: t('alignLeft'),
      alignCenter: t('alignCenter'),
      alignRight: t('alignRight'),
      bulletList: t('bulletList'),
      orderedList: t('orderedList'),
      textStyles: t('textStyles'),
      table: t('table'),
      addImage: t('addImage'),
      textColor: t('textColor'),
      column: t('column'),
      row: t('row'),
      cellColor: t('cellColor'),
      addAudio: t('addAudio'),
    }),
    [t],
  );

  return {
    t,
    forms,
    assignmentCreationLabels,
    editorLabels,
    isOpenFormRenameModal,
    isOpenFormDeleteModal,
    isOpenFormMoveModal,
    formIdSelected,
    formNameSelected,
    isModalVisible,
    setFormIdSelected,
    setFormNameSelected,
    setIsOpenFormRenameModal,
    setIsOpenFormDeleteModal,
    setIsOpenFormMoveModal,
    setModalVisibility,
    onCloseFormRenameModal,
    onCloseFormDeleteModal,
    onCloseFormMoveModal,
    onSelectForm,
    onCreateAssignmentSuccess,
  };
};
